import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, SplitText, useGSAP);
}

// Media query strings for gsap.matchMedia() — animate only when the user allows motion.
export const MOTION_OK = "(prefers-reduced-motion: no-preference)";

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ScrollTrigger config shared by all entrance reveals. Replayable:
// "play none none reverse" = play on enter, keep finished while scrolling
// past, and smoothly reverse back out when the user scrolls up above the
// section — so it animates in again the same way on the next way down.
export const revealTrigger = (trigger, start = "top 80%") => ({
  trigger,
  start,
  toggleActions: "play none none reverse",
});

// Standard editorial rise: content lifts and fades in.
export const riseFrom = (targets, opts = {}) =>
  gsap.from(targets, {
    y: 32,
    autoAlpha: 0,
    duration: 0.9,
    ease: "power3.out",
    ...opts,
  });

/**
 * Masked line reveal for display headings (SplitText `mask: "lines"`).
 * autoSplit re-splits when fonts finish loading or lines rewrap on resize;
 * returning the tween from onSplit lets SplitText rebuild it safely.
 * The target element must render its text via dangerouslySetInnerHTML so
 * React never reconciles the children SplitText replaces.
 */
export const maskedLineReveal = (target, { scrollTrigger, delay = 0 } = {}) =>
  SplitText.create(target, {
    type: "lines",
    mask: "lines",
    autoSplit: true,
    onSplit: (self) =>
      gsap.from(self.lines, {
        yPercent: 110,
        duration: 1.5,
        ease: "power4.out",
        stagger: 0.08,
        delay,
        ...(scrollTrigger ? { scrollTrigger } : {}),
      }),
  });

/*
 * Official GSAP horizontalLoop helper (trimmed: no Draggable support).
 * Builds a seamless, resize-tolerant xPercent loop across the given items.
 * https://gsap.com/docs/v3/HelperFunctions/helpers/seamlessLoop
 */
export function horizontalLoop(items, config) {
  items = gsap.utils.toArray(items);
  config = config || {};
  let tl = gsap.timeline({
      repeat: config.repeat,
      paused: config.paused,
      defaults: { ease: "none" },
      onReverseComplete: () =>
        tl.totalTime(tl.rawTime() + tl.duration() * 100),
    }),
    length = items.length,
    startX = items[0].offsetLeft,
    times = [],
    widths = [],
    xPercents = [],
    pixelsPerSecond = (config.speed || 1) * 100,
    snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1),
    totalWidth,
    curX,
    distanceToStart,
    distanceToLoop,
    item,
    i;
  gsap.set(items, {
    xPercent: (i, el) => {
      let w = (widths[i] = parseFloat(gsap.getProperty(el, "width", "px")));
      xPercents[i] = snap(
        (parseFloat(gsap.getProperty(el, "x", "px")) / w) * 100 +
          gsap.getProperty(el, "xPercent")
      );
      return xPercents[i];
    },
  });
  gsap.set(items, { x: 0 });
  totalWidth =
    items[length - 1].offsetLeft +
    (xPercents[length - 1] / 100) * widths[length - 1] -
    startX +
    items[length - 1].offsetWidth *
      gsap.getProperty(items[length - 1], "scaleX") +
    (parseFloat(config.paddingRight) || 0);
  for (i = 0; i < length; i++) {
    item = items[i];
    curX = (xPercents[i] / 100) * widths[i];
    distanceToStart = item.offsetLeft + curX - startX;
    distanceToLoop =
      distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
    tl.to(
      item,
      {
        xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
        duration: distanceToLoop / pixelsPerSecond,
      },
      0
    )
      .fromTo(
        item,
        {
          xPercent: snap(
            ((curX - distanceToLoop + totalWidth) / widths[i]) * 100
          ),
        },
        {
          xPercent: xPercents[i],
          duration:
            (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
          immediateRender: false,
        },
        distanceToLoop / pixelsPerSecond
      )
      .add("label" + i, distanceToStart / pixelsPerSecond);
    times[i] = distanceToStart / pixelsPerSecond;
  }
  tl.times = times;
  tl.progress(1, true).progress(0, true);
  if (config.reversed) {
    tl.vars.onReverseComplete();
    tl.reverse();
  }
  return tl;
}

/**
 * Seamless logo marquee that reacts to scroll: speeds up with scroll
 * velocity, reverses direction when the user scrolls up, and eases back
 * to cruising speed when scrolling settles.
 * Expects the scoped root to contain 2+ duplicated `.images` tracks.
 */
export function useMarqueeLoop(scopeRef, { speed = 0.8 } = {}) {
  useGSAP(
    (context, contextSafe) => {
      if (prefersReducedMotion()) return;
      const items = scopeRef.current
        ? Array.from(scopeRef.current.querySelectorAll(".images"))
        : [];
      if (items.length < 2) return;

      let speedTween;
      const build = () => {
        const loop = horizontalLoop(items, { repeat: -1, speed });
        ScrollTrigger.create({
          onUpdate(self) {
            const boost = gsap.utils.clamp(
              1,
              3.5,
              Math.abs(self.getVelocity()) / 400
            );
            if (speedTween) speedTween.kill();
            speedTween = gsap
              .timeline()
              .to(loop, { timeScale: boost * self.direction, duration: 0.25 })
              .to(loop, { timeScale: self.direction, duration: 0.8 }, "+=0.4");
          },
        });
      };

      // Logo widths must be final before the loop measures them.
      if (document.readyState === "complete") {
        build();
      } else {
        window.addEventListener("load", contextSafe(build), { once: true });
      }
    },
    { scope: scopeRef }
  );
}
